/**
 * TechDebtDispositionView — iter84.67 · Stewardship > Tech Debt.
 *
 * The DECISION surface for the tech-debt watchlist: every critical /
 * over-budget row gets a disposition workflow (Plan to resolve · Accept
 * issue) with a required reason that is written back into
 * /app/memory/tech_debt.md as the durable, git-tracked audit trail.
 * The metrics/detail table lives in Values > Tech Debt.
 */
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CalendarClock, HandMetal, RefreshCw, SlidersHorizontal, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const DISPOSITION_OPTIONS = [
    { value: "active", label: "Active debt", hint: "Counts as normal debt — no disposition on file." },
    { value: "planned", label: "Plan to resolve", hint: "A resolve-plan is on file. Row keeps its color but stops counting as an UNMANAGED critical in the Values Engine signal." },
    { value: "accepted", label: "Accept issue (no action)", hint: "Deliberate no-action call (e.g. code is sunsetting). Excluded from the Values Engine debt signal entirely." },
];

export function DispositionModal({ row, onClose, onSaved }) {
    const [disposition, setDisposition] = useState(row.disposition || "active");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (disposition !== "active" && !reason.trim()) {
            toast.error("A reason is required — it becomes the audit trail in tech_debt.md.");
            return;
        }
        setSaving(true);
        try {
            await api.setTechDebtDisposition({ path: row.path, disposition, reason: reason.trim() });
            toast.success(`${row.path} → ${disposition === "active" ? "active debt" : disposition}`);
            onSaved();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to save disposition");
        }
        setSaving(false);
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg" data-testid="tech-debt-disposition-modal">
                <DialogHeader>
                    <DialogTitle className="font-display uppercase tracking-tight">
                        Disposition · <span className="font-mono text-cyber text-base">{row.path}</span>
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        {row.current_loc != null ? `${row.current_loc} LOC vs ${row.target_max || "—"} target` : "not on disk"}
                        {row.disposition_raw ? <> · current: <span className="text-violet-300">{row.disposition_raw}</span></> : null}
                        {" — "}written to <code className="text-cyber">tech_debt.md</code>, no code edits needed.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                    {DISPOSITION_OPTIONS.map((opt) => (
                        <label
                            key={opt.value}
                            data-testid={`tech-debt-disposition-opt-${opt.value}`}
                            className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition-colors ${disposition === opt.value ? "border-cyber bg-cyber/5" : "border-zinc-800 hover:border-zinc-600"}`}
                        >
                            <input
                                type="radio"
                                name="td-disposition"
                                value={opt.value}
                                checked={disposition === opt.value}
                                onChange={() => setDisposition(opt.value)}
                                className="mt-0.5"
                            />
                            <span>
                                <span className="text-xs font-bold uppercase tracking-wider text-zinc-100 block">{opt.label}</span>
                                <span className="text-[10px] text-zinc-400 leading-snug block mt-0.5">{opt.hint}</span>
                            </span>
                        </label>
                    ))}
                    {disposition !== "active" && (
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            data-testid="tech-debt-disposition-reason"
                            placeholder={disposition === "accepted"
                                ? "Why is no action the right call? (e.g. 'ESPN path sunsets after the WC')"
                                : "What's the plan? (e.g. 'split into package next sprint, after WC')"}
                            className="w-full h-20 bg-black/40 border border-zinc-700 rounded p-2 text-xs text-zinc-200 focus:border-cyber outline-none"
                        />
                    )}
                    <div className="flex justify-end gap-2 pt-1">
                        <button onClick={onClose} className="brutal-btn-ghost text-xs py-1.5 px-3" data-testid="tech-debt-disposition-cancel">
                            Cancel
                        </button>
                        <button
                            onClick={save}
                            disabled={saving}
                            data-testid="tech-debt-disposition-save"
                            className="brutal-button text-xs py-1.5 px-3 uppercase tracking-wider bg-cyber/15 border-cyber hover:bg-cyber/25 disabled:opacity-50"
                        >
                            {saving ? "Saving…" : "Save disposition"}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function RowCard({ r, onDisposition }) {
    const statusMeta = r.status === "red"
        ? { cls: "border-heat/40", pill: "bg-heat/10 border-heat/40 text-heat", label: "Critical" }
        : r.status === "yellow"
            ? { cls: "border-blaze/40", pill: "bg-blaze/10 border-blaze/40 text-blaze", label: "Over budget" }
            : r.status === "accepted"
                ? { cls: "border-violet-500/30", pill: "bg-violet-500/10 border-violet-500/40 text-violet-300", label: "Accepted" }
                : { cls: "border-zinc-800", pill: "bg-cyber/10 border-cyber/40 text-cyber", label: "OK" };
    return (
        <div className={`brutal-card-light p-3 flex items-center justify-between gap-3 flex-wrap ${statusMeta.cls}`} data-testid={`stewardship-tech-debt-row-${r.path}`}>
            <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[11px] text-white">{r.path}</span>
                    <span className={`pill text-[9px] font-bold uppercase border ${statusMeta.pill}`}>{statusMeta.label}</span>
                    {r.disposition === "planned" && (
                        <span className="pill text-[9px] font-bold uppercase border bg-sky-500/10 border-sky-500/40 text-sky-300">
                            <CalendarClock className="w-2.5 h-2.5" /> Plan on file
                        </span>
                    )}
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                    {r.current_loc != null ? `${r.current_loc} LOC` : "not on disk"}
                    {r.target_max ? ` / ${r.target_max} target` : ""}
                    {r.disposition_raw ? <> · <span className="text-zinc-400">{r.disposition_raw}</span></> : null}
                </div>
            </div>
            <button
                data-testid={`tech-debt-disposition-btn-${r.path}`}
                onClick={() => onDisposition(r)}
                className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 whitespace-nowrap"
            >
                <SlidersHorizontal className="w-3 h-3" /> Disposition
            </button>
        </div>
    );
}

export function TechDebtDispositionView() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modalRow, setModalRow] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(await api.getPlatformTechDebt());
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load tech-debt watchlist");
        }
        setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    if (loading && !data) return <p className="text-xs text-zinc-500 italic">Loading watchlist…</p>;
    if (!data) return null;

    const needs = data.rows.filter((r) => (r.status === "red" || r.status === "yellow") && !r.disposition);
    const dispositioned = data.rows.filter((r) => r.disposition);
    needs.sort((a, b) => (a.status === "red" ? -1 : 1) - (b.status === "red" ? -1 : 1));

    return (
        <div className="space-y-4" data-testid="stewardship-tech-debt-view">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-xs text-zinc-400">
                    Disposition over-budget files: file a <span className="text-sky-300 font-bold">plan to resolve</span> or{" "}
                    <span className="text-violet-300 font-bold">accept the issue</span>. Reasons are written into{" "}
                    <code className="text-cyber">tech_debt.md</code> — the metrics table lives in Values → Tech Debt.
                </p>
                <button onClick={load} disabled={loading} className="brutal-btn-ghost text-[10px] py-1 px-2" data-testid="stewardship-tech-debt-refresh">
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-blaze" /> Needs disposition ({needs.length})
                </h3>
                {needs.length === 0 ? (
                    <p className="text-xs text-zinc-500 italic flex items-center gap-1" data-testid="stewardship-tech-debt-clear">
                        <CheckCircle2 className="w-3.5 h-3.5 text-cyber" /> Nothing over budget without a disposition — clean board.
                    </p>
                ) : (
                    <div className="space-y-2">{needs.map((r) => <RowCard key={r.path} r={r} onDisposition={setModalRow} />)}</div>
                )}
            </div>

            <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2 mb-2">
                    <HandMetal className="w-4 h-4 text-violet-300" /> Dispositioned ({dispositioned.length})
                </h3>
                {dispositioned.length === 0 ? (
                    <p className="text-xs text-zinc-500 italic">No standing dispositions.</p>
                ) : (
                    <div className="space-y-2">{dispositioned.map((r) => <RowCard key={r.path} r={r} onDisposition={setModalRow} />)}</div>
                )}
            </div>

            {modalRow && <DispositionModal row={modalRow} onClose={() => setModalRow(null)} onSaved={load} />}
        </div>
    );
}
